import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, Skeleton, Card, Grid, Avatar } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person'; // Regular User Icon
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'; // Admin Icon
import { makeStyles } from '@mui/styles';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
    marginTop: theme.spacing(4),
    maxWidth: '1200px',
    margin: '0 auto',
    
  },
  linkedUsersList: {
    padding: 0,
    marginTop: theme.spacing(2),
    textAlign: 'left',
  },
  errorText: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.main,
    textAlign: 'center',
  },
  adminLabel: {
    fontSize: '1.2rem',
    color: 'gold',
    fontStyle: 'italic',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  adminCard: {
    background: 'linear-gradient(to right, #f1c40f, #f39c12)', // Golden gradient for the admin
    padding: theme.spacing(3),
    borderRadius: '12px',
    boxShadow: theme.shadows[5],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(4),
    flexDirection: 'column',
    textAlign: 'center',
    position: 'relative',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: '0 15px 30px rgba(0, 0, 0, 0.2)',
    },
  },
  adminAvatar: {
    width: theme.spacing(10),
    height: theme.spacing(10),
    border: `5px solid ${theme.palette.common.white}`,
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[4],
  },
  adminText: {
    fontSize: '1.4rem',
    fontWeight: 'bold',
    color: theme.palette.common.white,
  },
  adminIcon: {
    color: theme.palette.common.white,
    fontSize: '2rem',
    marginBottom: theme.spacing(2),
  },
  adminIconContainer: {
    position: 'absolute',
    top: '-10px',
    right: '-10px',
    backgroundColor: 'gold',
    borderRadius: '50%',
    padding: '8px',
    boxShadow: '0 0 10px rgba(0,0,0,0.3)',
  },
  userCard: {
    padding: theme.spacing(2),
    margin: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    boxShadow: theme.shadows[2],
    borderRadius: '10px',
    transition: '0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[5],
    },
  },
  userCardAdmin: {
    backgroundColor: '#f5f5f5',
  },
  userIcon: {
    marginRight: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  userName: {
    fontWeight: 'normal',
    fontSize: '1rem',
  },
  gridContainer: {
    display: 'flex',
    minHeight: '50vh',
    flexDirection: 'row',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
}));

const ShowOrganizationUsers = ({ userId, onLogout }) => {

  useEffect(() => {
    window.scrollTo(0, 0);  // Scroll to top when this page is loaded
  }, []);

  
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .post(`${API_URL}/organization/users/`, { user_id: userId })
      .then((response) => {
        setUsers(response.data.users);
        setAdmin(response.data.admin);
      })
      .catch((err) => {
        setError('Failed to fetch users. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userId]);

  if (loading) {
    return (
      <div className={classes.container}>
        <Skeleton variant="rectangular" width="100%" height={200} />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="80%" />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Header1 userId={userId} onLogout={onLogout} />
     
      
            <Typography
                                  variant="h3"
                                  component="h1"
                                  align="center"
                                  fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
                                  sx={{
                                    backgroundImage: 'linear-gradient(45deg, #ff00ff, #00bfff)',
                                    mt:10,
                                    WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
                                    color: 'transparent',  // Makes the text itself transparent to show the gradient
                                  }}
                                  gutterBottom
                                >
                                  Organization Users
                                </Typography>

      {error && (
        <Typography variant="body2" color="error" align="center" className={classes.errorText}>
          {error}
        </Typography>
      )}

      {admin && (
        <Box mb={4}>
          <Card className={classes.adminCard}>
            <Avatar alt={`${admin.name} ${admin.surname}`} src="/path-to-avatar-image.jpg" className={classes.adminAvatar} />
            <Link to={`/public-view-user/${admin.id}`} style={{ textDecoration: "none" }}>
            <Typography variant="h5" className={classes.adminText}>
              {admin.name} {admin.surname}
            </Typography></Link>
            <Typography variant="body1" className={classes.adminText}>
              <strong>Admin</strong>
            </Typography>
            <Box className={classes.adminIconContainer}>
              <AdminPanelSettingsIcon className={classes.adminIcon} />
            </Box>
          </Card>
        </Box>
      )}

      <Grid container spacing={3} className={classes.gridContainer}>
        {users.length > 0 ? (
          users.map((user) => (
            <Grid item xs={12} sm={6} md={4} key={user.id}>
              <Card className={`${classes.userCard} ${user.id === admin.id ? classes.userCardAdmin : ''}`}>
                <ListItemIcon className={classes.userIcon}>
                  <PersonIcon color={user.id === admin.id ? 'gold' : 'default'} />
                </ListItemIcon>
                <Link to={`/public-view-user/${user.id}`} style={{ textDecoration: "none" }}>
                <ListItemText
                  primary={`${user.name} ${user.surname}`}
                  secondary={user.id === admin.id ? 'Admin' : 'User'}
                  className={user.id === admin.id ? classes.adminUser : ''}
                /></Link>
                {user.id === admin.id && (
                  <AdminPanelSettingsIcon className={classes.adminIcon} />
                )}
              </Card>
            </Grid>
          ))
        ) : (
          <Typography variant="body1">No users found in the organization.</Typography>
        )}
      </Grid>

      <Footer2 />
    </div>
  );
};

export default ShowOrganizationUsers;
